import React from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import App from "./App";
import { theme } from "./theme";
import { MobileContextProvider } from "@product-app/common/context/MobileContext";
import { TouchpointProvider } from "@product-app/common/workflows/telemetry/useTouchpoint";
import { orchestratorApi, fetchStoredFileResponse } from "./services/orchestratorApi";
import type { TouchpointEvent } from "@product-app/common/workflows/telemetry/touchpoints";
import { setStoredFileFetcher } from "@product-app/common/files/storedFileFetcher";
import "./styles.css";

// 26747 / P2.11: the real touchpoint client. `postUrl` is unused while
// `sink` is supplied (the provider only falls back to network delivery
// without one) but is required by the prop type, so it names the route the
// events describe even though delivery actually goes through
// `orchestratorApi.recordTouchpoints` — bearer auth + `X-Ora-Surface` are
// already handled by that client's shared `request()`. Fire-and-forget: the
// sink never awaits or rethrows, matching the provider's own contract.
function recordTouchpointsSink(events: TouchpointEvent[]): void {
  void orchestratorApi.recordTouchpoints(events).catch(() => {
    // Telemetry must never break the pane.
  });
}

// Story 25240: shared components load stored files through the relay with the
// bearer — the pane has no cookie session, so ora's default fetcher would 401.
setStoredFileFetcher(fetchStoredFileResponse);

// Wait for Office.js to initialize before rendering. When sideloaded into
// Outlook the global `Office` is defined; for non-Outlook previews we
// short-circuit so the app still mounts (you'll see "Select an email…").
const mount = () => {
  const root = ReactDOM.createRoot(document.getElementById("root")!);
  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* Provider is required by ora's CustomTooltip and other ora
            components shared into this app. The add-in renders inside
            an Outlook task pane (desktop or mobile mail clients). */}
        <MobileContextProvider isMobile={false} isTouchDevice={false}>
          <TouchpointProvider
            postUrl="/api/workflows/telemetry/touchpoints"
            sink={recordTouchpointsSink}
            surface="outlook_addin"
          >
            <App />
          </TouchpointProvider>
        </MobileContextProvider>
      </ThemeProvider>
    </React.StrictMode>,
  );
};

if (typeof Office !== "undefined" && Office.onReady) {
  Office.onReady(mount);
} else {
  mount();
}
